<template>
  <div :class="messageClasses">

    <template v-if="msgUser?._id === userData?._id">
      <div ref="massage" class="pe-2 msg-wrapper d-flex flex-column align-items-end">

        <div class="d-flex flex-row align-items-end justify-content-end mb-0 w-100">
          <div class="message-actions-side d-flex flex-row justify-content-end align-items-center pe-2 pb-1 gap-2">
            <button class="btn btn-sm btn-link p-0 text-secondary action-icon" @click="$emit('edit', { id: (id || tempId), msg, msgUser })" :title="t('buttons.edit')">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"></path><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path></svg>
            </button>

            <button class="btn btn-sm btn-link p-0 text-secondary action-icon" @click="$emit('reply', { id: (id || tempId), msg, msgUser, preview: ownPreview })" :title="t('buttons.reply')">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 17 4 12 9 7"></polyline><path d="M20 18v-2a4 4 0 0 0-4-4H4"></path></svg>
            </button>

            <div class="position-relative d-inline-block">
              <button class="btn btn-sm btn-link p-0 text-secondary action-icon" @click="showEmojiPicker = !showEmojiPicker" :title="t('buttons.reaction')" @blur="hideEmojiPicker">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M8 14s1.5 2 4 2 4-2 4-2"></path><line x1="9" y1="9" x2="9.01" y2="9"></line><line x1="15" y1="9" x2="15.01" y2="9"></line></svg>
              </button>
              <transition name="fade">
                <div v-if="showEmojiPicker" class="position-absolute bg-white shadow-sm border rounded p-1 d-flex flex-wrap justify-content-center" style="bottom: 100%; right: 0; width: 120px; z-index: 1050; margin-bottom: 5px;">
                  <span v-for="em in emojis" :key="em" class="p-1 emoji-hover" @click.stop="toggleReaction(em)" style="cursor: pointer; font-size: 16px; border-radius: 4px;">{{ em }}</span>
                </div>
              </transition>
            </div>
          </div>

          <div class="msg msg-mine d-flex flex-column align-items-end">
            <ReplyPreview
                v-if="replyTo"
                class="mb-2"
                :preview="replyTo.preview"
                :title="replyTo.user?.username || t('labels.replyUser', 'Користувач')"
                :text="replyTo.text"
                title-color="#198754"
                background="rgba(255, 255, 255, 0.45)"
            />

            <div class="w-100 text-end mb-1" style="font-size: 9px; color: #6c757d;">
              <span v-if="editedAt" class="me-1">(відредаговано)</span>{{ parseTime(time) }}
            </div>

            <div v-if="image" class="media-message w-100">
              <div v-if="image.type && image.type.startsWith('video/')">
                <video class="mb-2 w-100 rounded" :src="'/' + image.path" controls playsinline preload="metadata" height="150"></video>
              </div>
              <div v-else>
                <img :data-fancybox="'chatGallery-'+ msgUser?._id" class="mb-2 w-100 mw-100 rounded" :src="'/' + image.path" :alt="image._id" />
              </div>
            </div>

            <div v-if="media && media.length > 0" class="media-grid-container mb-2 w-100">
              <div :class="['media-grid', `grid-${Math.min(media.length, 4)}`]">
                <div v-for="file in media" :key="file._id" class="media-item position-relative overflow-hidden zoom-hover-effect">
                  <video v-if="file.type && file.type.startsWith('video/')" class="rounded w-100 h-100" style="object-fit: cover;" :src="'/' + file.path" controls playsinline></video>
                  <template v-else>
                    <img :data-fancybox="'chatGallery-'+ id" class="rounded w-100 h-100" style="object-fit: cover; aspect-ratio: 1; cursor: pointer;" :src="'/' + file.path" />
                    <div class="hover-overlay d-flex align-items-center justify-content-center position-absolute top-0 start-0 w-100 h-100 rounded" style="pointer-events: none;">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line><line x1="11" y1="8" x2="11" y2="14"></line><line x1="8" y1="11" x2="14" y2="11"></line></svg>
                    </div>
                  </template>
                </div>
              </div>
            </div>

            <div v-if="audio" class="media-message w-100">
              <vue-plyr @play="onAudioPlayInChat">
                <audio controls preload="metadata" style="width: 100%; max-width: 300px; min-width: 240px;">
                  <source :src="'/' + audio.path" :type="audio.type || 'audio/mp3'"/>
                </audio>
              </vue-plyr>
            </div>

            <div v-if="msg" :class="{'mt-1': image || audio || (media && media.length > 0)}" class="text-start">{{ msg }}</div>
            <div v-if="groupedReactions.length > 0" class="d-flex flex-wrap mt-1 gap-1 justify-content-end">
              <div v-for="r in groupedReactions" :key="r.emoji" @click="toggleReaction(r.emoji)"
                   class="reaction-badge rounded-pill px-2 d-flex align-items-center shadow-sm"
                   :class="{'active-reaction': r.iReacted}">
                <span class="me-1" style="font-size: 12px;">{{ r.emoji }}</span>
                <span class="fw-bold" style="font-size: 10px;" :class="r.iReacted ? 'text-primary' : 'text-muted'">{{ r.count }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="message-meta d-flex align-items-center flex-wrap justify-content-end mt-1" style="gap: 8px; font-size: 11px; color: #adb5bd;">
          <template v-if="readBy && readBy.length > 0">
            <template v-for="viewer in readBy" :key="getViewerId(viewer)">
                <span v-if="typeof viewer === 'object' && viewer.username && getViewerId(viewer) !== msgUser?._id"
                      class="d-inline-flex align-items-center text-secondary">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" class="me-1" viewBox="0 0 16 16"><path d="M10.5 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0z"/><path d="M0 8s3-5.5 8-5.5S16 8 16 8s-3 5.5-8 5.5S0 8 0 8zm8 3.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z"/></svg>
                  {{ viewer.username }} <span v-if="viewer.readAt" class="ms-1" style="font-size: 9px;">({{ formatReadTime(viewer.readAt) }})</span>
                </span>
            </template>
          </template>

          <span>
              <svg v-if="isMessageRead" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#198754" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 12l5 5l10 -10"></path><path d="M2 12l5 5m5 -5l5 -5"></path></svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12l5 5l10 -10"></path></svg>
          </span>
        </div>
      </div>
      <div class="ms-1 user-avatar flex-shrink-0">{{ userNameLetters }}</div>
    </template>


    <template v-else>
      <div class="user-avatar me-1 flex-shrink-0">{{ userNameLetters }}</div>
      <div class="msg-wrapper ps-2 d-flex flex-column align-items-start w-100">

        <div class="d-flex flex-row align-items-end justify-content-start mb-0 w-100">
          <div class="msg msg-other d-flex flex-column align-items-start">

            <ReplyPreview
                v-if="replyTo"
                class="mb-2"
                :preview="replyTo.preview"
                :title="replyTo.user?.username || t('labels.replyUser', 'Користувач')"
                :text="replyTo.text"
                title-color="#0b5ed7"
                background="rgba(13, 110, 253, 0.05)"
            />

            <div class="w-100 text-start mb-1" style="font-size: 9px; color: #6c757d;">
              <strong class="text-primary me-1">{{ msgUser?.username }}</strong>
              <span v-if="editedAt" class="me-1">(відредаговано)</span>{{ parseTime(time) }}
            </div>

            <div v-if="image" class="media-message w-100">
              <div v-if="image.type && image.type.startsWith('video/')">
                <video class="mb-2 w-100 rounded" :src="'/' + image.path" controls playsinline preload="metadata" height="150"></video>
              </div>
              <div v-else>
                <img :data-fancybox="'chatGallery-'+ msgUser?._id" class="mb-2 w-100 mw-100 rounded" :src="'/' + image.path" :alt="image._id" />
              </div>
            </div>

            <div v-if="media && media.length > 0" class="media-grid-container mb-2 w-100">
              <div :class="['media-grid', `grid-${Math.min(media.length, 4)}`]">
                <div v-for="file in media" :key="file._id" class="media-item position-relative overflow-hidden zoom-hover-effect">
                  <video v-if="file.type && file.type.startsWith('video/')" class="rounded w-100 h-100" style="object-fit: cover;" :src="'/' + file.path" controls playsinline></video>
                  <template v-else>
                    <img :data-fancybox="'chatGallery-'+ id" class="rounded w-100 h-100" style="object-fit: cover; aspect-ratio: 1; cursor: pointer;" :src="'/' + file.path" />
                    <div class="hover-overlay d-flex align-items-center justify-content-center position-absolute top-0 start-0 w-100 h-100 rounded" style="pointer-events: none;">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line><line x1="11" y1="8" x2="11" y2="14"></line><line x1="8" y1="11" x2="14" y2="11"></line></svg>
                    </div>
                  </template>
                </div>
              </div>
            </div>

            <div v-if="audio" class="media-message w-100">
              <vue-plyr @play="onAudioPlayInChat">
                <audio controls preload="metadata" style="width: 100%; max-width: 300px; min-width: 240px;">
                  <source :src="'/' + audio.path" :type="audio.type || 'audio/mp3'"/>
                </audio>
              </vue-plyr>
            </div>

            <div v-if="msg" :class="{'mt-1': image || audio || (media && media.length > 0)}" class="text-start">{{ msg }}</div>
            <div v-if="groupedReactions.length > 0" class="d-flex flex-wrap mt-1 gap-1">
              <div v-for="r in groupedReactions" :key="r.emoji" @click="toggleReaction(r.emoji)"
                   class="reaction-badge rounded-pill px-2 d-flex align-items-center shadow-sm"
                   :class="{'active-reaction': r.iReacted}">
                <span class="me-1" style="font-size: 12px;">{{ r.emoji }}</span>
                <span class="fw-bold" style="font-size: 10px;" :class="r.iReacted ? 'text-primary' : 'text-muted'">{{ r.count }}</span>
              </div>
            </div>
          </div>

          <div class="message-actions-side d-flex flex-row justify-content-start align-items-center ps-2 pb-1 gap-2">
            <button class="btn btn-sm btn-link p-0 text-secondary action-icon" @click="$emit('reply', { id: (id || tempId), msg, msgUser, preview: ownPreview })" :title="t('buttons.reply')">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 17 4 12 9 7"></polyline><path d="M20 18v-2a4 4 0 0 0-4-4H4"></path></svg>
            </button>

            <div class="position-relative d-inline-block">
              <button class="btn btn-sm btn-link p-0 text-secondary action-icon" @click="showEmojiPicker = !showEmojiPicker" :title="t('buttons.reaction')" @blur="hideEmojiPicker">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M8 14s1.5 2 4 2 4-2 4-2"></path><line x1="9" y1="9" x2="9.01" y2="9"></line><line x1="15" y1="9" x2="15.01" y2="9"></line></svg>
              </button>
              <transition name="fade">
                <div v-if="showEmojiPicker" class="position-absolute bg-white shadow-sm border rounded p-1 d-flex flex-wrap justify-content-center" style="bottom: 100%; left: 0; width: 120px; z-index: 1050; margin-bottom: 5px;">
                  <span v-for="em in emojis" :key="em" class="p-1 emoji-hover" @click.stop="toggleReaction(em)" style="cursor: pointer; font-size: 16px; border-radius: 4px;">{{ em }}</span>
                </div>
              </transition>
            </div>
          </div>
        </div>

        <div class="message-meta d-flex align-items-center flex-wrap justify-content-start mt-1" style="gap: 8px; font-size: 11px; color: #adb5bd;">
          <template v-if="readBy && readBy.length > 0">
            <template v-for="viewer in readBy" :key="getViewerId(viewer)">
                <span v-if="typeof viewer === 'object' && viewer.username && getViewerId(viewer) !== msgUser?._id && getViewerId(viewer) !== userData?._id"
                      class="d-inline-flex align-items-center text-secondary">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" class="me-1" viewBox="0 0 16 16"><path d="M10.5 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0z"/><path d="M0 8s3-5.5 8-5.5S16 8 16 8s-3 5.5-8 5.5S0 8 0 8zm8 3.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z"/></svg>
                  {{ viewer.username }} <span v-if="viewer.readAt" class="ms-1" style="font-size: 9px;">({{ formatReadTime(viewer.readAt) }})</span>
                </span>
            </template>
          </template>
        </div>

      </div>
    </template>
  </div>
</template>

<script>
import helpers from "../../.././helpers.js"
import {log} from "@/utils/clientLogger.js";
import {inject} from "vue";
import {useI18n} from 'vue-i18n';
import ReplyPreview from "@/components/order-chat/ReplyPreview.vue";
import {buildReplyPreview} from "@/components/order-chat/replyPreview.js";

export default {
  name: "ChatMessage",
  components: {ReplyPreview},
  data() {
    return {
      showEmojiPicker: false,
      emojis: ['👍', '❤️', '🔥', '😂', '😢', '🙏', '👎', '🎉']
    }
  },
  setup() {
    const socket = inject('$socket');
    const { t } = useI18n();

    return { socket, t };
  },
  computed: {
    groupedReactions() {
      if (!this.reactions || !this.reactions.length) return [];
      const groups = {};
      this.reactions.forEach(r => {
        if (!groups[r.emoji]) groups[r.emoji] = { emoji: r.emoji, count: 0, users: [], iReacted: false };
        groups[r.emoji].count++;
        // Перевіряємо чи поточний юзер (ти) ставив цю реакцію
        const rUserId = r.user?._id || r.user;
        if (this.userData && String(rUserId) === String(this.userData._id)) {
          groups[r.emoji].iReacted = true;
        }
      });
      return Object.values(groups);
    },
    userData() {
      return this.$store.getters.userData;
    },
    isMessageRead() {
      if (!this.readBy) return false;

      if (Array.isArray(this.readBy) && this.readBy.length === 0) return true;

      const authorId = String(this.msgUser?._id);
      return this.readBy.some(u => {
        const userId = String(u._id || u);
        return userId !== authorId;
      });
    },
    messageClasses() {
      log.info('View:ChatMessage:messageClasses', {
        'payload': {
          'Message author': this.msgUser,
          'Current user': this.userData,
          'Comparison result': this.msgUser?._id === this.userData?._id,
        }
      });

      return {
        'd-flex align-items-center': true,
        'text-right justify-content-end': this.msgUser?._id === this.userData?._id
      };
    },
    userNameLetters() {
      const name = this.msgUser?.username || '??';
      return name[0]?.toUpperCase() + (name[1] || '');
    },
    ownPreview() {
      return buildReplyPreview({ image: this.image, media: this.media, audio: this.audio });
    },
    mediaType() {
      if (this.image?.type && this.image.type.startsWith('video/')) return 'video';
      if (['image/jpeg', 'image/png'].includes(this.image?.type)) return 'image';
      if (this.audio?.type && this.audio.type.startsWith('audio/')) return 'audio';

      return null;
    },
  },
  methods: {
    hideEmojiPicker() {
      setTimeout(() => {
        this.showEmojiPicker = false;
      }, 200);
    },
    toggleReaction(emoji) {
      this.$emit('react', { id: this.id || this.tempId, emoji });
      this.showEmojiPicker = false;
    },
    formatReadTime(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });
    },
    formatFullDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleString('uk-UA', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    },
    getViewerName(viewer) {
      if (typeof viewer === 'string') return 'Прочитано';
      return viewer.username || viewer.login || 'Прочитано';
    },
    getViewerId(viewer) {
      return viewer._id || viewer;
    },
    parseTime(time) {
      return helpers.convertTime(time);
    },
    onAudioPlayInChat() {
      if (!this.audio || !this.socket || !this.orderId) {
        log.warn('ChatMessage:onAudioPlay', 'Missing audio, socket, or orderId', { id: this.audio?.id, order: this.orderId });
        return;
      }

      const currentUserId = this.userData?._id;

      const alreadyRead = this.audio.readBy?.find(user => user._id === currentUserId);

      if (alreadyRead) {
        return;
      }

      log.info('ChatMessage:onAudioPlay', 'Sending audio:read', { fileId: this.audio.id, orderId: this.orderId });

      this.socket.emit('audio:read', {
        fileId: this.audio._id,
        orderId: this.orderId
      });
    }
  },
  emits: ['reply', 'edit', 'react'],
  props: {
    id: String,
    msgUser: Object,
    msg: String,
    time: Object,
    image: Object,
    media: Array,
    audio: Object,
    orderId: String,
    readBy: Array,
    replyTo: Object,
    editedAt: String,
    reactions: { type: Array, default: () => [] }
  }
}
</script>

<style scoped lang="scss">
.msg-wrapper {
  max-width: 85%;
  min-width: 150px;
  margin-bottom: 6px;
}

.user-avatar {
  width: 32px;
  height: 32px;
  background: var(--bs-dark);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 11px;
  font-weight: bold;
}

.msg {
  font-size: 13px;
  padding: 8px 12px;
  border-radius: 12px;
  font-weight: 400;
  color: #212529;
  word-break: break-word;
  box-shadow: 0 1px 2px rgba(0,0,0,0.08);
}

.msg-mine {
  background-color: #d1e7dd;
  border-bottom-right-radius: 2px;
}

.msg-other {
  background-color: #ffffff;
  border: 1px solid #dee2e6;
  border-bottom-left-radius: 2px;
}

.media-message img, .media-message video {
  max-height: 250px;
  object-fit: cover;
}

video:fullscreen {
  object-fit: contain !important;
}

video:-webkit-full-screen {
  object-fit: contain !important;
}

.message-actions-side {
  width: auto; /* ЗМІНЕНО: раніше було 24px */
  flex-shrink: 0;

  .action-icon {
    transition: transform 0.15s ease-in-out, color 0.15s;
    color: #adb5bd !important;

    &:hover {
      color: #0d6efd !important;
      transform: scale(1.15);
    }
  }
}

.media-grid-container {
  max-width: 300px;
  width: 100%;
}
.media-grid {
  display: grid;
  gap: 4px;
}

/* 1 Файл (Велике фото) */
.grid-1 {
  grid-template-columns: 1fr;
  .media-item { height: 250px; }
}

.grid-2 {
  grid-template-columns: 1fr 1fr;
  .media-item { height: 150px; }
}

.grid-3 {
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 150px 100px;
  .media-item:first-child {
    grid-column: span 2;
    height: 100%;
  }
}

.grid-4 {
  grid-template-columns: 1fr 1fr;
  grid-auto-rows: 100px;
}

.media-item {
  width: 100%;
  height: 100%;
}

.media-item img, .media-item video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.zoom-hover-effect {
  img {
    transition: transform 0.3s ease;
  }
  .hover-overlay {
    background: rgba(0, 0, 0, 0.4);
    opacity: 0;
    transition: opacity 0.3s ease;

    svg {
      transform: scale(0.5);
      transition: transform 0.3s ease;
    }
  }

  &:hover {
    img {
      transform: scale(1.05);
    }
    .hover-overlay {
      opacity: 1;

      svg {
        transform: scale(1);
      }
    }
  }
}

.emoji-hover {
  transition: transform 0.1s, background-color 0.1s;
  &:hover { transform: scale(1.2); background-color: #f8f9fa; }
}
.reaction-badge {
  cursor: pointer;
  background: #ffffff;
  border: 1px solid #dee2e6;
  height: 20px;
  transition: all 0.2s ease;
  user-select: none;

  &:hover { background: #f8f9fa; border-color: #adb5bd; }
  &.active-reaction { background: #e0f0ff; border-color: #86b7fe; }
}
</style>